import { firebasestore, firestore } from "../firebase/firebase";


/**
 * Gets history data of a given cryptocurrency via firestore and Cryptowatch API intelligently.
 * @param  {Object} curr The object of current cryptocurrency.
 * @param  {React.Dispatch<Array>} setdata The React state update function for final data.
 * @param  {React.Dispatch<Boolean>} setShow The React state update function for boolean flag of content to be shown or not.
 * @param  {Boolean} full Whether to get complete data of data of last 30 days.
 * @param  {Boolean} olhc Whether to get olhc data for olhc graph or single point data for line plot.
 */
export function GetHistory(curr, setdata, setShow, full = false, olhc = false) {
    Promise.all([
        firestore.collection('History').doc(curr.info.symbol).get(),
        firestore.collection('History').doc(curr.info.symbol).collection('Timings').where('active', '==', true).orderBy('datapoints', "desc").limit(1).get()
    ])  // Get Symbol timing, Get symbol history from exchange with most number of datapoints.
        .then(([sym, docs]) => {
            if (!docs.empty) { // If no active exchange is present in dataset
                //console.log("Document Symbol:", curr.market+':'+curr.pair, "Document data:", doc.data());
                docs.forEach((doc) => {     // For each doc in Query Snapshot (which has only one doc.)
                    if ((firebasestore.Timestamp.now().seconds - doc.data().last.seconds) <= 86400) {   // Has data been updated within one day?
                        firestore.collection('History').doc(curr.info.symbol).collection(doc.id).where(firebasestore.FieldPath.documentId(), ">=", (full ? 0 : (doc.data().last.seconds - 7776000)).toString()).get() // Get buckets containing data of 90 days.
                            .then((querySnapshot) => {
                                let d = []; // final data array
                                let seenTimestamps = new Set();
                                querySnapshot.forEach((dataPoint) => {  // For each bucket
                                    // dataPoint.data() is never undefined for query doc snapshots
                                    //console.log(dataPoint.id, " => ", dataPoint.data();
                                    dataPoint.data().content.forEach((val) => { // Add data of bucket into array d;
                                        if (!seenTimestamps.has(val.CloseTime)) {
                                            seenTimestamps.add(val.CloseTime);
                                            let dt = new Date(val.CloseTime * 1000);
                                            d.push(olhc ? [dt, val.LowPrice, val.OpenPrice, val.ClosePrice, val.HighPrice] : [dt, val.ClosePrice]);
                                        }
                                    });
                                });
                                let D = full ? d : d.slice(-30);
                                D.unshift(olhc ? ['Date', 'L-H, O-C', 'Open', 'Close', 'High'] : ['Date', 'Price']);    // Data formatting for google charts.
                                setdata(D);
                            });
                    } else {    // If not updated within a day, We will have to get new data from API, Merge it to database and show the data.
                        Promise.all([firestore.collection('History').doc(curr.info.symbol).collection(doc.id).where(firebasestore.FieldPath.documentId(), ">=", (full ? 0 : (doc.data().last.seconds - 7776000)).toString()).get(), fetch('https://min-api.cryptocompare.com/data/v2/histoday?fsym=' + curr.info.symbol.toUpperCase() + '&tsym=USD&limit=2000&e=' + doc.id)])
                            // Get data of last 90 days which is present in the dataset. Get data from API which is not there in the dataset.
                            .then(([dFromStore, dFromApi]) => Promise.all([dFromStore, dFromApi.json()]))
                            .then(([dFromStore, dFromApi]) => {
                                if (dFromApi.Response === "Success" && dFromApi.Data && dFromApi.Data.Data) {
                                    let bucket; // last bucket
                                    let d = [];
                                    let seenTimestamps = new Set();
                                    
                                    dFromStore.forEach((dataPoint) => {
                                        // dataPoint.data() is never undefined for query doc snapshots
                                        //console.log(dataPoint.id, " => ", dataPoint.data();
                                        bucket = dataPoint;
                                        dataPoint.data().content.forEach((val) => {     // For Each Bucket, add data to array d;
                                            if (!seenTimestamps.has(val.CloseTime)) {
                                                seenTimestamps.add(val.CloseTime);
                                                let dt = new Date(val.CloseTime * 1000);
                                                d.push(olhc ? [dt, val.LowPrice, val.OpenPrice, val.ClosePrice, val.HighPrice] : [dt, val.ClosePrice]);
                                            }
                                        });
                                    });

                                    if ((firebasestore.Timestamp.now().seconds - dFromApi.Data.Data[dFromApi.Data.Data.length - 1].time) <= 86400) {  // If we got updated data from API (within last 24 hours)
                                        dFromApi.Data.Data.filter(x => x.time >= (firebasestore.Timestamp.now().seconds - 2678400)).forEach((obj) => {    // Add data of last 31 days. (last 30 days + current 1 day)
                                            if (!seenTimestamps.has(obj.time)) {
                                                seenTimestamps.add(obj.time);
                                                let dt = new Date(obj.time * 1000);
                                                d.push(olhc ? [dt, obj.low, obj.open, obj.close, obj.high] : [dt, obj.close]);
                                            }
                                        });
                                        d = d.slice(0, d.length - 1);   // remove the data of current day, since it is not final.
                                    }
                                    
                                    if (d.length > 1) {
                                        let D = full ? d : d.slice(-30);
                                        D.unshift(olhc ? ['Date', 'L-H, O-C', 'Open', 'Close', 'High'] : ['Date', 'Price']);    // Data formatting for google charts.
                                        setdata(D);
                                    } else {
                                        setShow(false);
                                    }

                                    // Start Uploading to firestore.
                                    let batch = firestore.batch();  // Upload data in Batch.
                                    let count = bucket.data().content.length; // days count in last bucket.
                                    let v = bucket.data().content; // vector with content of 60 days
                                    let flag = false;
                                    let first = bucket.id;
                                    if (bucket.data().content.length == 60) {
                                        count = 0; // days count
                                        v = []; // vector with content of 60 days
                                        flag = false;
                                        first = dFromApi.Data.Data[0].time;
                                    }
                                    let it = 0;
                                    while (it !== dFromApi.Data.Data.length - 1) {
                                        let obj = dFromApi.Data.Data[it];
                                        if (flag) {
                                            first = obj.time;
                                            flag = false;
                                        }
                                        v.push({
                                            CloseTime: obj.time,
                                            OpenPrice: obj.open,
                                            HighPrice: obj.high,
                                            LowPrice: obj.low,
                                            ClosePrice: obj.close,
                                            Volume: obj.volumefrom,
                                            QuoteVolume: obj.volumeto
                                        });
                                        if (++count == 60) {
                                            batch.set(firestore.collection('History').doc(curr.info.symbol).collection(doc.id).doc(first.toString()), {
                                                content: v,
                                            }, { merge: true });
                                            flag = true;
                                            count = 0;
                                            v = [];
                                        }
                                        it++;
                                    }

                                    if (it !== 0) {
                                        if (v.length !== 0) {
                                            batch.set(firestore.collection('History').doc(curr.info.symbol).collection(doc.id).doc(first.toString()), {
                                                content: v,
                                            }, { merge: true });
                                        }

                                        let isUpdated = (firebasestore.Timestamp.now().seconds - dFromApi.Data.Data[dFromApi.Data.Data.length - 1].time) <= 1296000; // wait for 15 days. If no data is found thereafter, Mark endpoint as inactive.


                                        //if(!dFromApi.Data.Data[it - 1]) console.log("Wanted dt", dFromApi.Data.Data, it, dFromApi.Data.Data[it - 1]);
                                        batch.set(firestore.collection('History').doc(curr.info.symbol).collection('Timings').doc(doc.id), {
                                            last: firebasestore.Timestamp.fromMillis(dFromApi.Data.Data[it - 1].time * 1000),
                                            active: isUpdated,
                                            datapoints: firebasestore.FieldValue.increment(dFromApi.Data.Data.length),
                                        }, { merge: true });

                                        if (!isUpdated) {   // If endpoint is inactive. Add it in inactive endpoints collection for further review.
                                            batch.set(firestore.collection('Inactive Endpoints').doc(curr.info.symbol + ':' + doc.id), {
                                                setOn: firebasestore.Timestamp.now(),
                                            }, { merge: true });
                                        }

                                        batch.commit()
                                            .then(() => {
                                                // console.log("Data updated for " + doc.id + ":" + curr.info.symbol);
                                            }).catch((err) => console.log("Error while data updating for " + doc.id + ":" + curr.info.symbol, err));

                                        // console.log('Hey', curr.info.symbol, doc.id);
                                    }
                                } else { // If API is giving error.
                                    setShow(false);
                                    // console.log("Cryptowatch Error : ", dFromApi);
                                }
                            });
                    }
                });
            } else if (!sym.exists) {   // New cryptocurrency found. Show data from API and upload data in database.
                // doc.data() will be undefined in this case

                let last = firebasestore.Timestamp.now().seconds + 172800;
                let isDataPresent = false; // Does any exchange has some history data?
                async function getHistory(market) {
                    let res = fetch('https://min-api.cryptocompare.com/data/v2/histoday?fsym=' + curr.info.symbol.toUpperCase() + '&tsym=USD&limit=2000&e=' + market).then((result) => result.json());
                    res = await res;
                    // console.log("Yeiks...", market, curr.pair, res.Data);
                    if (res.Response === "Success" && res.Data && res.Data.Data) {
                        // console.log('Yey..', (firebasestore.Timestamp.now().seconds - res.Data.Data[res.Data.Data.length - 1].time) <= 86400, res.Data.Data[0].time, last, res.Data.Data[0].time < last);
                        if ((firebasestore.Timestamp.now().seconds - res.Data.Data[res.Data.Data.length - 1].time) <= 86400 && res.Data.Data[0].time < last) {

                            let d = [];
                            last = res.Data.Data[0].time;
                            res.Data.Data.filter(x => x.time >= (firebasestore.Timestamp.now().seconds - 2592000)).map((obj) => {
                                let dt = new Date(obj.time * 1000);
                                //console.log('date:', Math.floor(dt.getTime() / 1000), obj.time);
                                d.push(olhc ? [dt, obj.low, obj.open, obj.close, obj.high] : [dt, obj.close]);
                            });
                            d = d.slice(0, d.length - 1); // remove the data of current day, since it is not final.
                            // Data is already filtered to last 30 days by timestamp above, no need to slice again
                            d.unshift(olhc ? ['Date', 'L-H, O-C', 'Open', 'Close', 'High'] : ['Date', 'Price']);
                            setdata(d);
                            isDataPresent = true;
                        }
                        //testBatchedWrites(res.result[86400], curr.info.symbol, market).then(() => console.log("Completed!!"), (err) => console.log(err));
                        
                        // Start Uploading to firestore.
                        let batch = firestore.batch();
                        let count = 0; // days count
                        let v = []; // vector with content of 60 days
                        let flag = false;
                        let first = res.Data.Data[0].time;
                        let it = 0;
                        while (it !== res.Data.Data.length - 1) {
                            let obj = res.Data.Data[it];
                            if (flag) {
                                first = obj.time;
                                flag = false;
                            }
                            v.push({
                                CloseTime: obj.time,
                                OpenPrice: obj.open,
                                HighPrice: obj.high,
                                LowPrice: obj.low,
                                ClosePrice: obj.close,
                                Volume: obj.volumefrom,
                                QuoteVolume: obj.volumeto
                            });
                            if (++count == 60) {
                                batch.set(firestore.collection('History').doc(curr.info.symbol).collection(market).doc(first.toString()), {
                                    content: v,
                                });
                                flag = true;
                                count = 0;
                                v = [];
                            }
                            it++;
                        }

                        if (v.length !== 0) {
                            batch.set(firestore.collection('History').doc(curr.info.symbol).collection(market).doc(first.toString()), {
                                content: v,
                            });
                        }

                        let isUpdated = (firebasestore.Timestamp.now().seconds - res.Data.Data[res.Data.Data.length - 1].time) <= 1296000; // wait for 15 days.

                        batch.set(firestore.collection('History').doc(curr.info.symbol).collection('Timings').doc(market), {
                            first: firebasestore.Timestamp.fromMillis(res.Data.Data[0].time * 1000),
                            last: firebasestore.Timestamp.fromMillis(res.Data.Data[it - 1].time * 1000),
                            datapoints: res.Data.Data.length,
                            active: isUpdated,
                        });

                        if (!isUpdated) {
                            batch.set(firestore.collection('Inactive Endpoints').doc(curr.info.symbol + ':' + market), {
                                setOn: firebasestore.Timestamp.now(),
                            });
                        }

                        return await batch.commit();
                    }
                }

                // Rate limiting: Process markets sequentially with 250ms delay between calls (4 calls/sec to stay under 5/sec limit)
                async function processMarketsSequentially() {
                    for (let i = 0; i < curr.market.length; i++) {
                        try {
                            await getHistory(curr.market[i]);
                            // Add delay between calls to respect rate limit (250ms = 4 calls/second)
                            if (i < curr.market.length - 1) {
                                await new Promise(resolve => setTimeout(resolve, 500));
                            }
                        } catch (err) {
                            console.log("Error fetching history for", curr.market[i], err);
                        }
                    }
                    
                    if (isDataPresent) {
                        // console.log('Data Uploaded for ', curr.info.symbol);
                    } else {
                        setShow(false);
                        // console.log(curr.info.symbol + " will not be shown..");
                    }
                }
                
                processMarketsSequentially();
            } else {
                setShow(false);
                // console.log(curr.info.symbol + " will not be shown..");
            }
        }).catch((err) => console.log(err));
}
